/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ComponentInternalInstance } from 'vue'

export type MyInputValue<T, K = T> = ReturnType<typeof useMyInput<T, K>>
type RefsInstance<T, K> = { myInputExpose: None | (() => MyInputValue<T, K> | None) }
export type RefInstanceInput<T, K> = None | MaybeRef<None | RefsInstance<T, K>>
export type MyInputErrorValue<T> = { msg: stringI18n | (() => stringI18n); isErr: (val: T) => boolean }
export type MyInputError<T> = MyInputErrorValue<T> | boolean | None | '' | 0
type Refs = ReturnType<typeof useRefs>
export type MyFormProvidedValue = {
  activated: () => boolean
  refs: () => Refs | None
}
export const myFormContext: InjectionKey<MyFormProvidedValue | undefined> = Symbol('myFormContext')

const translations = () => {
  return useT2Base({
    Empty: 'Tuščia',
  })
}

type Translations = ReturnType<typeof translations>

export function getMyInputFromRef<T, K = T>(ref: RefInstanceInput<T, K> | Record<string, any> | null): MyInputValue<any> | None {
  return unref(ref)?.myInputExpose?.()
}

function objEq(val1: unknown, val2: unknown) {
  return helpers.toObject(val1)?.render === helpers.toObject(val2)?.render
}

function validateInputRef(instance: ComponentInternalInstance, form: MyFormProvidedValue) {
  const refs = form.refs()
  if (!refs) return

  let keyInForm: undefined | string
  const inputCtx = getMyInputFromRef(instance.exposed) as MyInputValue<any> | None
  const inputId = inputCtx?.id
  if (inputId) {
    for (const key in refs) {
      if (inputId === getMyInputFromRef(refs[key]?.value)?.id) {
        keyInForm = key
        break
      }
    }
  }

  const error = (err: Record<string, any>) => {
    console.error('[inputId]', err)
    console.trace('inputId')
    const el = helpers.getHTMLElement(instance?.proxy)
    if (el) el.style.background = 'red'
  }

  if (!keyInForm) return error({ keyInForm, instance, form })

  const comp = (refs.__refs as unknown as Parameters<typeof useRefs>[0])[keyInForm]

  const ref = refs[keyInForm]?.value
  const isOkRef = objEq(comp, ref?.$options) // comp === ref?.$options

  // console.log('isOkRefisOkRefisOkRef', isOkRef, comp, comp?.render === ref?.$options?.render)

  const err = !ref ? 'no ref' : !isOkRef ? 'bad ref' : !inputCtx ? 'not an input' : undefined
  if (err) return error({ keyInForm, isOkRef, err })
}

function isEmptyDefault(i: unknown) {
  if (!helpers.isDef(i)) return true
  switch (typeof i) {
    case 'boolean': {
      return false
    }
    case 'number': {
      return i === 0
    }
    case 'string': {
      return i === ''
    }
    case 'object': {
      if (Array.isArray(i)) return i.length === 0
      return helpers.isObjectEmpty(i)
    }
  }
  return false
}

function validateInput<T>(value: T, validators: None | MyInputError<T>[], inp: Pick<MyInputInput<any>, 'optional' | 'empty' | 'error'>, t2: Translations): string | undefined {
  if (inp.empty ? inp.empty(value) : isEmptyDefault(value)) {
    return getFuncValue(inp.optional) ? undefined : t2('Empty')
  }
  const err = validators?.find((i) => i && typeof i === 'object' && i.isErr(value))
  const res = typeof err === 'object' ? getFuncValue(err?.msg) : inp.error?.()
  return res?.length ? res : undefined
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useMyInput<T, K = T>(inp: {
  label: string | (() => string | undefined)
  optional: boolean | (() => boolean | None)
  value: () => T
  error?: () => string | None
  validate?: () => None | MyInputError<T>[]
  empty?: (i: T) => boolean
  input?: (i: T) => K
}) {
  const ctx = inject(myFormContext, () => undefined, true)
  const data = shallowReactive({ active: false })

  function activated(): boolean {
    return data.active || !!ctx?.activated()
  }

  provide(myFormContext, { refs: () => undefined, activated })
  let isExposed = false

  const expose = {
    id: useId() || generateId(),
    inp,
    ctx,
    activated,
    activate() {
      if (!data.active) data.active = true
    },
    expose(): RefsInstance<T, K> {
      isExposed = true
      return { myInputExpose: () => expose }
    },
  }

  if (import.meta.dev) {
    const instsnce = getCurrentInstance()
    onMounted(() => {
      window.setTimeout(() => {
        if (ctx && instsnce) validateInputRef(instsnce, ctx)
        if (!isExposed) throw new Error('Input not exposed')
      }, 20)
    })
  }

  return expose
}

type MyInputInput<T, K = T> = Parameters<typeof useMyInput<T, K>>[0]

export function getFormValueOneLabel<T, K>(instance: RefInstanceInput<T, K>): { label: string | undefined; id: string } | undefined {
  const input = getMyInputFromRef(instance)
  const inp = input?.inp
  if (!input || !inp) return undefined
  return { label: getFuncValue(inp.label), id: input.id }
}

export function getFormValueOneWithError<T, K>(instance: RefInstanceInput<T, K>): { error: string; value?: never } | { value: K; error?: never } {
  const t2 = translations()
  const input = getMyInputFromRef(instance)
  const inp = input?.inp
  if (!input || !inp) {
    return { error: !inp ? 'Missing ref: no input' : 'Missing ref' }
  }
  const value = inp.value()
  const error = validateInput(value, inp.validate?.(), inp, t2) || ''
  if (error) return { error }
  return { value: inp.input ? inp.input(value) : (value as unknown as K) }
}

export function getFormValueOne<T, K>(instance: RefInstanceInput<T, K>, fb?: () => K): K {
  const res = getFormValueOneWithError(instance)
  if ('error' in res) {
    const input = getMyInputFromRef(instance)
    input?.activate()
    if (fb) return fb()
    const label = getFormValueOneLabel(instance)?.label
    const err = helpers.joinString(' ', label, res.error)
    if (import.meta.dev) console.error('[getFormValueOne]', { input, inp: input?.inp, instance, err, res })
    throw new Error(err)
  }
  return res.value
}

export function getFormValue<T extends Record<string, globalThis.Ref<None | RefsInstance<any, any>>>, KeysOmit extends Readonly<(keyof T)[]> = []>(
  refs: T,
  omit?: KeysOmit
): {
  [key in Exclude<keyof T, KeysOmit[number]>]: ReturnType<NN2<ReturnType<NN2<T[key], 'value', 'myInputExpose'>>, 'inp', 'input'>>
} {
  try {
    const data: Record<string, any> = {}

    for (const key in refs) {
      if (key === '__refs' || omit?.includes(key)) continue
      data[key] = getFormValueOne(refs[key])
    }

    return data as ReturnType<typeof getFormValue<T, KeysOmit>>
  } catch (err) {
    const vald = validateFormRefsErrors({ refs, omit: omit as any })
    if (import.meta.dev) {
      if (vald.print) console.table(vald.print)
      else console.error('[getFormValue]', { err, refs, omit })
    }
    throw err
  }
}

type RefsParams = Parameters<typeof useRefs>[0]

export function validateFormRefsErrors(props: { refs: Refs; omit?: string[] | readonly string[] | None }): {
  errors?: Record<string, any>[]
  print?: Record<string, any>[]
} {
  const { refs, omit } = props
  const components = refs.__refs as unknown as RefsParams
  if (!components) return {}

  const print: Record<string, any>[] = []
  const errors: Record<string, any>[] = []

  let someErrorr = false
  for (const name in components) {
    const comp = components[name]
    const ref = refs[name]?.value
    const isok = objEq(comp, ref?.$options)
    const inputCtx = getMyInputFromRef(ref)
    const error = !ref ? 'no ref' : !isok ? 'bad ref' : !inputCtx ? 'not an input' : undefined
    const omitOne = omit?.includes(name) ?? undefined
    if (error && !(omitOne && error === 'no ref')) someErrorr = true
    if (error) errors.push({ name, error, omit: omitOne })
    print.push({ name, isok, inputCtx, error, omit: omitOne })
  }

  return someErrorr ? { errors, print } : {}
}

export function useMyInputValidate<Inp extends MyInputValue<any, any>>(
  inputCtx2: Inp | (() => Inp | None),
  validate?: {
    disabled?: () => boolean | None
    focus?: () => boolean
    lazy?: boolean
    activated?: boolean
  }
): Readonly<globalThis.Ref<string>> {
  const t2 = translations()
  const ctx = (): Inp | None => getFuncValue(inputCtx2)
  const lazy = !!validate?.lazy
  const lang = useLocale()
  const data = shallowReactive({
    errors: '',
    blured: validate?.activated || !validate?.focus,
  })

  const [validators] = useValueSynced2((): MyInputErrorValue<any>[] => helpers.arr(ctx()?.inp.validate?.(), (i) => (i && helpers.isObject(i) ? i : undefined)), { unproxy: (i) => i })

  if (validate?.focus && !data.blured) {
    const timer = useTimeout()
    const offBlur = watch(
      () => validate.focus?.(),
      (i, prev) => {
        timer.set(() => {
          if (prev && !i) {
            data.blured = true
            nextTick(() => offBlur())
          }
        }, 10)
      }
    )
  }

  watch(
    () => ({
      lang: lang.value,
      validatorsArr: validators.value,
      value: lazy ? undefined : ctx()?.inp.value(),
      disabled: validate?.disabled?.(),
      blured: data.blured,
      activated: ctx()?.activated() || validate?.activated,
    }),
    ({ value, disabled, activated, blured, validatorsArr }) => {
      if (disabled) {
        if (data.errors) data.errors = ''
      } else if (activated || blured) {
        const inp = ctx()?.inp
        const res = lazy ? inp?.value() : value
        const err = (inp && validateInput(res, validatorsArr, inp, t2)) || ''
        if (err !== data.errors) data.errors = err
      }
    },
    { immediate: validate?.activated }
  )

  return computed(() => data.errors)
}

export function myInputPass<T, K>(instance: () => RefInstanceInput<T, K>): RefsInstance<T, K> {
  return { myInputExpose: () => getMyInputFromRef(instance()) }
}
